import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import displayError from '../../utils/ErrorHandler';
import UserContext from '../../contexts/UserContext';
import { useNavigate, Link } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { n2 } from '../../utils/fmt';

const ListDishes = () => {
  const [dishes, setDishes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);

  const goToCreateDish = () => {
    navigate("/central/create-dish");
  };

  useEffect(() => {
    setLoading(true)
    axios.get(`${process.env.REACT_APP_API_URI}/api/dish/viewdishes`, { withCredentials: true })
      .then(response => {
        setLoading(false)
        setDishes(response.data.data || []);
      })
      .catch((err) => {
        setLoading(false);
        const errorMessage = displayError(err, navigate, setUser);
        if (errorMessage) {
          setMessage(errorMessage);
        }
      });
  }, []);

  useEffect(() => {
      async function startup() {
        if (!user) {
          await authenticate(setUser, navigate);
        }
      }
      startup();
    }, [user, navigate, setUser]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const getBadgeClass = (category) => {
    switch (category) {
      case 'starter':
        return 'bg-blue-500 text-blue-100';
      case 'main course':
        return 'bg-green-600 text-green-100';
      case 'dessert':
        return 'bg-purple-600 text-purple-100';
      default:
        return 'bg-gray-500 text-gray-100';
    }
  };

  // Export the full dish master (all dishes + their ingredients & recipe) to a styled Excel file.
  const handleExportToExcel = () => {
    if (!dishes.length) {
      setMessage('No dishes to export.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const headers = [
        'Dish Name', 'Category', 'Sub Category', 'Cooking Qty', 'Cooking Unit',
        'Ingredient Name', 'Ingredient Qty', 'Ingredient Unit', 'Recipe'
      ];
      // Columns that describe the dish itself (rather than one ingredient) - these get
      // merged vertically across all of that dish's ingredient rows.
      const dishColIdxs = [0, 1, 2, 3, 4, 8];

      const dataRows = [];
      const dishRowSpans = []; // { startRow, rowCount, recipeLen } per dish, indices into dataRows

      dishes.forEach(dish => {
        const ingredients = dish.ingredients && dish.ingredients.length > 0 ? dish.ingredients : [null];
        const startRow = dataRows.length;

        ingredients.forEach(ing => {
          dataRows.push([
            dish.name || '',
            dish.category || '',
            dish.sub_category || '',
            dish.quantity != null ? n2(dish.quantity) : '',
            dish.unit || '',
            ing ? (ing.name?.name || 'Unknown item') : 'No ingredients listed',
            ing && ing.quantity != null ? n2(ing.quantity) : '',
            ing?.name?.unit || '',
            dish.recipe || '',
          ]);
        });

        dishRowSpans.push({ startRow, rowCount: ingredients.length, recipeLen: (dish.recipe || '').length });
      });

      const totalIngredientLines = dataRows.length;
      const titleText = `Dish Master Export — ${new Date().toLocaleDateString('en-IN')} (${dishes.length} dishes, ${totalIngredientLines} ingredient lines)`;

      const headerRowIdx = 2; // title (0), blank (1), header (2)
      const aoa = [[titleText], [], headers, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Merge the title across all columns, and merge each dish's own columns down
      // across its ingredient rows so the sheet reads like a grouped recipe sheet.
      const merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
      dishRowSpans.forEach(({ startRow, rowCount }) => {
        if (rowCount > 1) {
          const excelStart = headerRowIdx + 1 + startRow;
          const excelEnd = excelStart + rowCount - 1;
          dishColIdxs.forEach(c => merges.push({ s: { r: excelStart, c }, e: { r: excelEnd, c } }));
        }
      });
      ws['!merges'] = merges;

      const titleStyle = {
        font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: '1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'center' },
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: '2F75B5' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'D9D9D9' } },
          bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
          left: { style: 'thin', color: { rgb: 'D9D9D9' } },
          right: { style: 'thin', color: { rgb: 'D9D9D9' } },
        },
      };
      const thinGreyBorder = {
        top: { style: 'thin', color: { rgb: 'E5E7EB' } },
        bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
        left: { style: 'thin', color: { rgb: 'E5E7EB' } },
        right: { style: 'thin', color: { rgb: 'E5E7EB' } },
      };
      const bandA = { fill: { patternType: 'solid', fgColor: { rgb: 'FFFFFF' } }, border: thinGreyBorder };
      const bandB = { fill: { patternType: 'solid', fgColor: { rgb: 'F2F7FC' } }, border: thinGreyBorder };

      if (ws['A1']) ws['A1'].s = titleStyle;
      headers.forEach((_, c) => {
        const ref = XLSX.utils.encode_cell({ r: headerRowIdx, c });
        if (ws[ref]) ws[ref].s = headerStyle;
      });

      dishRowSpans.forEach((span, dishIdx) => {
        const band = dishIdx % 2 === 0 ? bandA : bandB;
        for (let i = 0; i < span.rowCount; i++) {
          const excelRow = headerRowIdx + 1 + span.startRow + i;
          dataRows[span.startRow + i].forEach((_, c) => {
            const ref = XLSX.utils.encode_cell({ r: excelRow, c });
            if (!ws[ref]) return;
            let alignment = { vertical: 'top' };
            if (c === 3 || c === 6) alignment = { horizontal: 'right', vertical: 'top' }; // qty columns
            else if (c === 8) alignment = { horizontal: 'left', vertical: 'top', wrapText: true }; // recipe
            else alignment = { horizontal: 'left', vertical: 'top' };
            ws[ref].s = { ...band, alignment };
          });
        }
      });

      ws['!cols'] = [
        { wch: 26 }, // Dish Name
        { wch: 16 }, // Category
        { wch: 16 }, // Sub Category
        { wch: 12 }, // Cooking Qty
        { wch: 12 }, // Cooking Unit
        { wch: 26 }, // Ingredient Name
        { wch: 14 }, // Ingredient Qty
        { wch: 14 }, // Ingredient Unit
        { wch: 50 }, // Recipe
      ];
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: headerRowIdx, c: 0 }, e: { r: headerRowIdx, c: headers.length - 1 } }) };
      // Give rows with a recipe a bit more height so wrapped text isn't clipped by default.
      ws['!rows'] = [{ hpt: 26 }, { hpt: 6 }, { hpt: 20 }, ...dataRows.map((_, i) => {
        const span = dishRowSpans.find(s => i >= s.startRow && i < s.startRow + s.rowCount);
        const linesEstimate = span ? Math.ceil(span.recipeLen / 55) : 1;
        return { hpt: Math.min(120, Math.max(18, linesEstimate * 15)) };
      })];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Dish Master');

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const fileName = `Dish_Master_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);

      setMessage('Dishes exported successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error exporting dishes to Excel:', error);
      setMessage('Error exporting dishes to Excel. Please try again.');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const filteredDishes = dishes.filter(dish =>
    dish?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dish?.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
    {loading ? (
      <div className='flex justify-center items-center h-[70vh] w-full'>
        <p>Loading Please Wait...</p>
      </div>
    ) : (
          <div className="p-4">
      {message && (
        <div className={`px-4 py-3 mx-0 mb-4 rounded relative border ${
          message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')
            ? 'bg-red-100 border-red-300 text-red-500'
            : 'bg-green-100 border-green-300 text-green-600'
        }`} role="alert">
          <span className="block sm:inline">{message}</span>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          className="flex-grow p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb"
          placeholder="Search dishes..."
          value={searchQuery}
          onChange={handleSearch}
        />
        <button
          onClick={handleExportToExcel}
          disabled={dishes.length === 0}
          className="flex items-center justify-center gap-2 whitespace-nowrap
                     bg-gradient-to-r from-orange-500 to-orange-600
                     hover:from-orange-600 hover:to-orange-700
                     disabled:from-gray-400 disabled:to-gray-500
                     disabled:cursor-not-allowed
                     text-white font-semibold py-2 px-5 rounded-lg
                     shadow transform transition duration-200
                     hover:scale-105 focus:outline-none focus:ring-4
                     focus:ring-orange-300 disabled:hover:scale-100"
        >
          <span className="text-lg">📊</span>
          Export to Excel
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">

        {filteredDishes.map(dish => (
          <Link to={`/central/dish/${dish._id}`} key={dish._id}>
            <div className="bg-white p-4 rounded-lg shadow-md border border-fmb-light flex justify-between items-center hover:bg-gray-50 cursor-pointer">
              <div>
                <h2 className="text-lg font-semibold text-fmb">{dish.name}</h2>
                <div className="text-sm text-gray-700">
                  <span>{dish.quantity} {dish.unit}</span>
                </div>
              </div>
              {dish.category && (
                <div className={`text-sm font-medium px-3 py-1 rounded-full ${getBadgeClass(dish.category)}`}>
                  {dish.category}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Floating Action Button */}
      <div className="fab-container" onClick={goToCreateDish}>
        <button className="fab-button bg-fmb">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <span className="fab-text text-fmb">Add New Dish</span>
      </div>
    </div>
    )}
    </>
  );
};

export default ListDishes;
